import { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Select,
    Switch,
    Button,
    Group,
    Stack,
    Text,
    Alert,
    Loader,
    Center,
    Title,
    Paper,
    Divider,
    Box
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconMail, IconCheck, IconRefresh } from '@tabler/icons-react';
import classes from './EmailNotificationSettings.module.css';
import {
    EmailNotificationConfig,
    UpdateEmailNotificationConfigRequest,
    EMAIL_FIELD_CONFIGS,
    EmailFieldConfig,
    getAllEmailNotificationConfigs,
    saveEmailNotificationConfig
} from '../../services/EmailNotificationConfigService';
import useTransientStore from '../../data/transcientStore';
import { useTranslation } from "../../hooks/useTranslation";


interface AddressSystemOption {
    value: string;
    label: string;
}

interface CategoryFields {
    identification: EmailFieldConfig[];
    location: EmailFieldConfig[];
    dates: EmailFieldConfig[];
    personnel: EmailFieldConfig[];
    additional: EmailFieldConfig[];
}

const CATEGORY_TEXT_CODES: Record<keyof CategoryFields, string> = {
    identification: 'LABEL_EMAIL_CATEGORY_IDENTIFICATION',
    location: 'LABEL_EMAIL_CATEGORY_LOCATION',
    dates: 'LABEL_EMAIL_CATEGORY_DATES',
    personnel: 'LABEL_EMAIL_CATEGORY_PERSONNEL',
    additional: 'LABEL_EMAIL_CATEGORY_ADDITIONAL'
};

export default function EmailNotificationSettings() {
    const [configs, setConfigs] = useState<EmailNotificationConfig[]>([]);
    const [selectedAddressSystemId, setSelectedAddressSystemId] = useState<string | null>(null);
    const [currentConfig, setCurrentConfig] = useState<EmailNotificationConfig | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [originalConfig, setOriginalConfig] = useState<EmailNotificationConfig | null>(null);

    const currentUserId = useTransientStore((state) => state.currentUserId);
    const selectedAddressSystem = useTransientStore((state) => state.selectedAddressSystem);
    const addrIdFromStore = selectedAddressSystem?.id || 0;
    const { getTranslation } = useTranslation({
        pageId: 351,
        addressSystemId: addrIdFromStore || 0,
        autoLoad: true,
    });

    // Group fields by category
    const fieldsByCategory = useMemo<CategoryFields>(() => {
        return EMAIL_FIELD_CONFIGS.reduce((acc, field) => {
            acc[field.category].push(field);
            return acc;
        }, {
            identification: [],
            location: [],
            dates: [],
            personnel: [],
            additional: []
        } as CategoryFields);
    }, []);

    // Load all configurations
    const loadConfigs = useCallback(async () => {
        setLoading(true);
        try {
            const data = await getAllEmailNotificationConfigs();
            setConfigs(data);

            // Auto-select first address system if none selected
            if (data.length > 0 && !selectedAddressSystemId) {
                setSelectedAddressSystemId(data[0].addressSystemId.toString());
            }
        } catch (error) {
            console.error('Error loading email notification configs:', error);
            notifications.show({
                title: getTranslation('LABEL_EMAIL_ERROR'),
                message: getTranslation('LABEL_EMAIL_LOAD_ERROR'),
                color: 'red',
                icon: <IconAlertCircle size={16} />
            });
        } finally {
            setLoading(false);
        }
    }, [selectedAddressSystemId]);

    useEffect(() => {
        loadConfigs();
    }, []);

    // Update current config when address system changes
    useEffect(() => {
        if (selectedAddressSystemId) {
            const config = configs.find(c => c.addressSystemId.toString() === selectedAddressSystemId);
            if (config) {
                setCurrentConfig({ ...config });
                setOriginalConfig({ ...config });
                setHasChanges(false);
            }
        }
    }, [selectedAddressSystemId, configs]);

    // Address system options for dropdown
    const addressSystemOptions: AddressSystemOption[] = useMemo(() => {
        return configs.map(c => ({
            value: c.addressSystemId.toString(),
            label: c.addressSystemName || `Address System ${c.addressSystemId}`
        }));
    }, [configs]);

    // Handle field toggle
    const handleFieldToggle = (fieldKey: keyof EmailNotificationConfig) => {
        if (!currentConfig) return;

        const updatedConfig = {
            ...currentConfig,
            [fieldKey]: !currentConfig[fieldKey]
        };
        setCurrentConfig(updatedConfig);
        setHasChanges(true);
    };

    // Handle select all in category
    const handleSelectAllInCategory = (category: keyof CategoryFields, selectAll: boolean) => {
        if (!currentConfig) return;

        const updatedConfig = { ...currentConfig };
        fieldsByCategory[category].forEach(field => {
            (updatedConfig as any)[field.key] = selectAll;
        });
        setCurrentConfig(updatedConfig);
        setHasChanges(true);
    };

    // Check if all fields in a category are selected
    const isCategoryAllSelected = (category: keyof CategoryFields): boolean => {
        if (!currentConfig) return false;
        return fieldsByCategory[category].every(field => currentConfig[field.key] as boolean);
    };

    // Save configuration
    const handleSave = async () => {
        if (!currentConfig) return;

        setSaving(true);
        try {
            const request: UpdateEmailNotificationConfigRequest = {
                addressSystemId: currentConfig.addressSystemId,
                includeObjectName: currentConfig.includeObjectName,
                includeAddressSystem: currentConfig.includeAddressSystem,
                includeStatus: currentConfig.includeStatus,
                includeContractName: currentConfig.includeContractName,
                includeStreetName: currentConfig.includeStreetName,
                includeOrderNumber: currentConfig.includeOrderNumber,
                includeSiteNumber: currentConfig.includeSiteNumber,
                includeNetworkNumber: currentConfig.includeNetworkNumber,
                includePlannedDate: currentConfig.includePlannedDate,
                includeStartDate: currentConfig.includeStartDate,
                includeReadyDate: currentConfig.includeReadyDate,
                includeApprovedDate: currentConfig.includeApprovedDate,
                includeModifiedDate: currentConfig.includeModifiedDate,
                includeForeman: currentConfig.includeForeman,
                includeContractorForeman: currentConfig.includeContractorForeman,
                includeProjectManager: currentConfig.includeProjectManager,
                includeSubscriber: currentConfig.includeSubscriber,
                includeDescription: currentConfig.includeDescription,
                includeObjectLength: currentConfig.includeObjectLength,
                includeType: currentConfig.includeType,
                modifiedByUserId: currentUserId
            };

            const savedConfig = await saveEmailNotificationConfig(request);

            // Update configs list
            setConfigs(prevConfigs =>
                prevConfigs.map(c =>
                    c.addressSystemId === savedConfig.addressSystemId ? savedConfig : c
                )
            );
            setOriginalConfig(savedConfig);
            setHasChanges(false);

            notifications.show({
                title: getTranslation('LABEL_EMAIL_SUCCESS'),
                message: getTranslation('LABEL_EMAIL_SAVE_SUCCESS'),
                color: 'green',
                icon: <IconCheck size={16} />
            });
        } catch (error) {
            console.error('Error saving email notification config:', error);
            notifications.show({
                title: getTranslation('LABEL_EMAIL_ERROR'),
                message: getTranslation('LABEL_EMAIL_SAVE_ERROR'),
                color: 'red',
                icon: <IconAlertCircle size={16} />
            });
        } finally {
            setSaving(false);
        }
    };

    // Reset to original
    const handleReset = () => {
        if (originalConfig) {
            setCurrentConfig({ ...originalConfig });
            setHasChanges(false);
        }
    };

    // Count enabled fields
    const enabledFieldsCount = useMemo(() => {
        if (!currentConfig) return 0;
        return EMAIL_FIELD_CONFIGS.filter(f => currentConfig[f.key] as boolean).length;
    }, [currentConfig]);

    // Render field category
    const renderCategory = (category: keyof CategoryFields) => {
        const fields = fieldsByCategory[category];
        const allSelected = isCategoryAllSelected(category);

        return (
            <Box className={classes.categorySection} key={category}>
                <div className={classes.categoryHeader}>
                    <Text className={classes.categoryTitle}>{getTranslation(CATEGORY_TEXT_CODES[category])}</Text>
                    <Button
                        variant="subtle"
                        size="xs"
                        className={classes.selectAllButton}
                        onClick={() => handleSelectAllInCategory(category, !allSelected)}
                    >
                        {allSelected ? getTranslation('LABEL_EMAIL_DESELECT_ALL') : getTranslation('LABEL_EMAIL_SELECT_ALL')}
                    </Button>
                </div>
                <div className={classes.fieldGrid}>
                    {fields.map(field => (
                        <div className={classes.fieldItem} key={field.key}>
                            <Switch
                                checked={currentConfig ? (currentConfig[field.key] as boolean) : false}
                                onChange={() => handleFieldToggle(field.key)}
                                size="sm"
                            />
                            <Text
                                className={classes.switchLabel}
                                onClick={() => handleFieldToggle(field.key)}
                            >
                                {getTranslation(field.textCode)}
                            </Text>
                        </div>
                    ))}
                </div>
            </Box>
        );
    };

    // Render email preview
    const renderPreview = () => {
        if (!currentConfig) return null;

        const enabledFields = EMAIL_FIELD_CONFIGS.filter(f => currentConfig[f.key] as boolean);

        return (
            <Box className={classes.previewSection}>
                <Text className={classes.previewTitle}>
                    {getTranslation('LABEL_EMAIL_PREVIEW_TITLE')} ({enabledFieldsCount} {getTranslation('LABEL_EMAIL_FIELDS_ENABLED')})
                </Text>
                <Box className={classes.previewContent}>
                    {enabledFields.length === 0 ? (
                        <Text c="dimmed" ta="center">{getTranslation('LABEL_EMAIL_NO_FIELDS_SELECTED')}</Text>
                    ) : (
                        enabledFields.map(field => (
                            <div className={classes.previewField} key={field.key}>
                                <span className={classes.previewLabel}>{getTranslation(field.textCode)}:</span>
                                <span style={{ color: '#868e96' }}>{getTranslation('LABEL_EMAIL_VALUE_PLACEHOLDER')}</span>
                            </div>
                        ))
                    )}
                </Box>
            </Box>
        );
    };

    if (loading) {
        return (
            <Center className={classes.loader}>
                <Loader size="lg" />
            </Center>
        );
    }

    if (configs.length === 0) {
        return (
            <div className={classes.container}>
                <Alert icon={<IconAlertCircle size={16} />} color="yellow">
                    {getTranslation('LABEL_EMAIL_NO_ADDRESS_SYSTEMS')}
                </Alert>
            </div>
        );
    }

    return (
        <div className={classes.container}>
            <div className={classes.header}>
                <div>
                    <Title order={2} className={classes.title}>
                        <IconMail size={28} style={{ marginRight: 10, verticalAlign: 'middle' }} />
                        {getTranslation("LABEL_DASHBOARD_EMAIL_NOTIFICATION_SETTINGS") || "Email Notification Settings"}
                    </Title>
                    <Text className={classes.description}>
                        {getTranslation('LABEL_EMAIL_DESCRIPTION')}
                    </Text>
                </div>
                <Button
                    variant="subtle"
                    leftSection={<IconRefresh size={16} />}
                    onClick={loadConfigs}
                    loading={loading}
                >
                    {getTranslation('LABEL_EMAIL_REFRESH')}
                </Button>
            </div>

            <Paper className={classes.selectorContainer} p="md" withBorder>
                <Select
                    label={getTranslation('LABEL_EMAIL_SELECT_ADDRESS_SYSTEM') ?? undefined}
                    placeholder={getTranslation('LABEL_EMAIL_CHOOSE_ADDRESS_SYSTEM') ?? undefined}
                    data={addressSystemOptions}
                    value={selectedAddressSystemId}
                    onChange={setSelectedAddressSystemId}
                    searchable
                    clearable={false}
                    style={{ maxWidth: 400 }}
                />
            </Paper>

            {currentConfig && (
                <>
                    <Paper className={classes.configContainer} withBorder>
                        <Stack gap="lg">
                            {(Object.keys(fieldsByCategory) as Array<keyof CategoryFields>).map(category => (
                                renderCategory(category)
                            ))}
                        </Stack>

                        <Divider my="lg" />

                        <Group justify="flex-end" className={classes.buttonContainer}>
                            <Button
                                variant="outline"
                                onClick={handleReset}
                                disabled={!hasChanges || saving}
                            >
                                {getTranslation('LABEL_EMAIL_RESET')}
                            </Button>
                            <Button
                                onClick={handleSave}
                                loading={saving}
                                disabled={!hasChanges}
                                leftSection={<IconCheck size={16} />}
                            >
                                {getTranslation('LABEL_EMAIL_SAVE_CHANGES')}
                            </Button>
                        </Group>
                    </Paper>

                    {renderPreview()}
                </>
            )}
        </div>
    );
}
